import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import SupplierSelector from "../../components/CustomComponents/modal/SupplierSelector";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";

const CreatePurchaseOrder = () => {
  const navigate = useNavigate();
  const compID = useSelector((state) => state.auth.user?.userData?.compID);
  const currentUserName = useSelector((state) => state.auth.user?.userData?.userName);
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [loading, setLoading] = useState(false);

  // Form fields
  const [poName, setPoName] = useState("");
  const [deliveryDueDate, setDeliveryDueDate] = useState("");
  const [deliveryType, setDeliveryType] = useState("Standard");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [poStatus, setPoStatus] = useState("Draft");
  const [paymentStatus, setPaymentStatus] = useState("Unpaid");
  const [poAmount, setPoAmount] = useState("");
  const [paidAmount, setPaidAmount] = useState("");
  const [isSendEmail, setIsSendEmail] = useState(false);
  const [deliverToCompId, setDeliverToCompId] = useState("");

  // Modal states
  const [showSupplierModal, setShowSupplierModal] = useState(false);


  // Handle form submission with redirect option
  const handleSubmit = async (e, redirectToEdit = false) => {
    e.preventDefault();

    if (!poName.trim()) {
      showErrorToast("Please enter purchase order name");
      return;
    }

    if (!selectedSupplier) {
      showErrorToast("Please select a supplier");
      return;
    }

    try {
      setLoading(true);

      const newPOData = {
        PO_Name: poName,
        DeliveryDueDate: deliveryDueDate ? new Date(deliveryDueDate).toISOString() : null,
        PO_DeliveryType: deliveryType,
        SupplierID: parseInt(selectedSupplier.s_ID || selectedSupplier.Supplier_ID),
        SupplierName: selectedSupplier.s_Name || selectedSupplier.Supplier_Name,
        PO_status: poStatus,
        PO_Paymentstatus: paymentStatus,
        PO_Quantity: "0", // Default quantity
        PO_Amount: poAmount ? parseFloat(poAmount) : null,
        PO_PaidAmount: paidAmount ? parseFloat(paidAmount) : null,
        PO_IsSendEmail: isSendEmail,
        DeliverStockToCompID: deliverToCompId ? parseInt(deliverToCompId) : null,
        StaffUserName: currentUserName || "System",
        CompID: compID,
        CreatedBy: currentUserName || "System",
        ModifiedBy: currentUserName || "System",
        PurchaseOrderPayments: [], // Empty array for payments
        PurchaseOrderProductLists: [] // Empty array for product lists
      };

      const response = await axiosInstance.post(`api/PurchaseOrder/CreatePurchaseOrder`, newPOData);
      debugger;
      if (response.data && response.data.data) {
        const createdPO = response.data.data;

        showSuccessToast(`Purchase order "${poName}" created successfully`);

        if (redirectToEdit) {
          // Navigate to the update page for the newly created PO
          navigate(`/purchaseOrder/updatePurchaseOrder/${createdPO.pO_ID}`);
        } else {
          navigate("/purchaseOrder/managePurchaseOrders");
        }
      }
    } catch (error) {
      console.error("Error creating purchase order:", error);
      showErrorToast("Failed to create purchase order");
    } finally {
      setLoading(false);
    }
  };

  // Render breadcrumb with Save/Save and Edit buttons
  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/dashboard">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/purchaseOrder/managePurchaseOrders">Purchase Orders</Link>
          </li>
          <li className="breadcrumb-item active">Create Purchase Order</li>
        </ol>
        <h1 className="page-header mb-0">
          <i className="fa fa-plus-circle me-2"></i>
          Create New Purchase Order
        </h1>
      </div>
      <div className="ms-auto d-flex gap-2">
        <button
          onClick={(e) => handleSubmit(e, true)}
          className="btn btn-rounded px-4 rounded-pill"
          style={{
            backgroundColor: themeColor,
            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
            border: 'none',
            color: 'white',
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            boxShadow: `0 2px 8px ${themeColor}40`
          }}
          onMouseEnter={(e) => {
            e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
          }}
          onMouseLeave={(e) => {
            e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
          }}
        >
          Save and Edit
        </button>
        <button
          onClick={(e) => handleSubmit(e, false)}
          className="btn btn-rounded px-4 rounded-pill"
          style={{
            backgroundColor: themeColor,
            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
            border: 'none',
            color: 'white',
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            boxShadow: `0 2px 8px ${themeColor}40`
          }}
          onMouseEnter={(e) => {
            e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
          }}
          onMouseLeave={(e) => {
            e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
          }}
        >
          Save
        </button>
      </div>
    </div>
  );

  // Basic Information Panel
  const renderBasicInfoPanel = () => (
    <Panel className="mb-4">
      <PanelHeader
        style={{
          backgroundColor: themeColor,
          color: 'white',
          border: 'none',
          borderRadius: '10px 10px 0 0',
          padding: '15px 20px',
          fontWeight: '600',
          fontSize: '16px'
        }}
      >
        <i className="fas fa-info-circle me-2"></i>
        Basic Information
      </PanelHeader>
      <PanelBody
        style={{
          padding: '25px',
          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
          borderRadius: '0 0 10px 10px',
          border: `1px solid ${themeColor}20`
        }}
      >
        <div className="row g-4">
          <div className="col-md-6">
            <FormInput
              label="Purchase Order Name *"
              type="text"
              value={poName}
              onchange={(e) => setPoName(e.target.value)}
              placeHolder="Enter PO name"
              required
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Delivery Due Date"
              type="date"
              value={deliveryDueDate}
              onchange={(e) => setDeliveryDueDate(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                Delivery Type
              </label>
              <select
                className="form-select"
                value={deliveryType}
                onChange={(e) => setDeliveryType(e.target.value)}
                style={{
                  borderRadius: "8px",
                  border: isDarkMode ? '1px solid #404040' : '1px solid #D9D9D9',
                  background: isDarkMode ? '#2d3436' : '#F9F9F9',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  padding: '8px 12px'
                }}
              >
                <option value="Standard">Standard Delivery</option>
                <option value="Express">Express Delivery</option>
                <option value="Pickup">Pickup</option>
              </select>
            </div>
          </div>
        </div>
      </PanelBody>
    </Panel>
  );

  // Supplier Information Panel
  const renderSupplierInfoPanel = () => (
    <Panel className="mb-4">
      <PanelHeader
        style={{
          backgroundColor: themeColor,
          color: 'white',
          border: 'none',
          borderRadius: '10px 10px 0 0',
          padding: '15px 20px',
          fontWeight: '600',
          fontSize: '16px'
        }}
      >
        <i className="fas fa-truck me-2"></i>
        Supplier Information
      </PanelHeader>
      <PanelBody
        style={{
          padding: '25px',
          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
          borderRadius: '0 0 10px 10px',
          border: `1px solid ${themeColor}20`
        }}
      >
        <div className="row g-4">
          <div className="col-12">
            <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
              Supplier *
            </label>
            <SupplierSelector
              selectedSupplier={selectedSupplier}
              setSelectedSupplier={setSelectedSupplier}
              showModal={showSupplierModal}
              setShowModal={setShowSupplierModal}
              setLoading={setLoading}
            />
          </div>
        </div>
      </PanelBody>
    </Panel>
  );

  // Order Details Panel
  const renderOrderDetailsPanel = () => (
    <Panel className="mb-4">
      <PanelHeader
        style={{
          backgroundColor: themeColor,
          color: 'white',
          border: 'none',
          borderRadius: '10px 10px 0 0',
          padding: '15px 20px',
          fontWeight: '600',
          fontSize: '16px'
        }}
      >
        <i className="fas fa-file-alt me-2"></i>
        Order Details
      </PanelHeader>
      <PanelBody
        style={{
          padding: '25px',
          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
          borderRadius: '0 0 10px 10px',
          border: `1px solid ${themeColor}20`
        }}
      >
        <div className="row g-4">
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                Status
              </label>
              <select
                className="form-select"
                value={poStatus}
                onChange={(e) => setPoStatus(e.target.value)}
                style={{
                  borderRadius: "8px",
                  border: isDarkMode ? '1px solid #404040' : '1px solid #D9D9D9',
                  background: isDarkMode ? '#2d3436' : '#F9F9F9',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  padding: '8px 12px'
                }}
              >
                <option value="Draft">Draft</option>
                <option value="Pending">Pending Approval</option>
                <option value="Approved">Approved</option>
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                Payment Status
              </label>
              <select
                className="form-select"
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
                style={{
                  borderRadius: "8px",
                  border: isDarkMode ? '1px solid #404040' : '1px solid #D9D9D9',
                  background: isDarkMode ? '#2d3436' : '#F9F9F9',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  padding: '8px 12px'
                }}
              >
                <option value="Unpaid">Unpaid</option>
                <option value="Partial">Partial Payment</option>
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <FormInput
              label="Estimated Total Amount"
              type="number"
              step="0.01"
              value={poAmount}
              onchange={(e) => setPoAmount(e.target.value)}
              placeHolder="0.00"
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Initial Paid Amount"
              type="number"
              step="0.01"
              value={paidAmount}
              onchange={(e) => setPaidAmount(e.target.value)}
              placeHolder="0.00"
            />
          </div>
        </div>
      </PanelBody>
    </Panel>
  );

  return (
    <div className="container-fluid p-3">
      {loading && <Preloader />}

      {/* Updated Breadcrumb with Save/Save and Edit buttons */}
      {renderBreadcrumb()}

      {/* Basic Information Panel */}
      {renderBasicInfoPanel()}

      {/* Supplier Information Panel */}
      {renderSupplierInfoPanel()}

      {/* Order Details Panel */}
      {renderOrderDetailsPanel()}
    </div>
  );
};

export default CreatePurchaseOrder;